import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { catchError, map } from 'rxjs/operators';
import { of } from 'rxjs';
import { UserAccess } from '../interfaces/state/entities/user-access.interface';
import { UserAccessStore } from '../services/user-access/user-access.store';
import { RegisteredApplicationStore } from '../services/registered-applications/registered-application.store';
import { PreferredAppEntry } from '../utils/user-access-entry.storage';

function preferredEntryChoices(access: UserAccess): PreferredAppEntry[] {
  const c: PreferredAppEntry[] = [];
  if (access.isDeveloper) c.push('developer');
  if (access.isSupport) c.push('support');
  if (access.isAdmin) c.push('admin');
  return c;
}

export const projectAreaGuard: CanActivateFn = () => {
  const store = inject(UserAccessStore);
  const regStore = inject(RegisteredApplicationStore);
  const router = inject(Router);

  return store.load().pipe(
    map((access) => {
      if (!access.isDeveloper && !access.isSupport && !access.isAdmin) {
        return router.parseUrl('/no-access');
      }

      const choices = preferredEntryChoices(access);
      const pref = store.preferredEntry();

      const isAdminOnly = access.isAdmin && !access.isDeveloper && !access.isSupport;
      if ((pref === 'admin' && access.isAdmin) || isAdminOnly) {
        return router.parseUrl('/admin');
      }

      if (choices.length > 1) {
        if (!pref || !choices.includes(pref)) {
          return router.parseUrl('/select-role');
        }
      }

      const needsRegisteredApplication =
        pref === 'support' ||
        (choices.length === 1 && !access.isDeveloper && access.isSupport);

      if (needsRegisteredApplication && regStore.selectedId() === null) {
        return router.parseUrl('/registered-applications');
      }

      return true;
    }),
    catchError(() => of(router.parseUrl('/no-access')))
  );
};
